import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import usePageTitle from '../hooks/usePageTitle';
import { ChevronRight, Sparkles, Download } from 'lucide-react';
import { getDownloadPlatform, fetchLatestRelease, buildDownloadInfo } from '../utils/downloadPlatform';

const SillyDragon = () => {
  const { t } = useLanguage();
  usePageTitle('General AI - Silly Dragon');
  const videoRef = useRef(null);
  const [videoError, setVideoError] = useState(false);
  const [downloadModal, setDownloadModal] = useState(null);
  const [release, setRelease] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchLatestRelease().then(setRelease);
  }, []);

  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;
    const onError = () => setVideoError(true);
    const onLoadedData = () => setVideoError(false);
    el.addEventListener('error', onError);
    el.addEventListener('loadeddata', onLoadedData);
    return () => {
      el.removeEventListener('error', onError);
      el.removeEventListener('loadeddata', onLoadedData);
    };
  }, []);

  const handleDownloadClick = () => {
    const platform = getDownloadPlatform();
    const { filename, url } = buildDownloadInfo(platform, release);
    setDownloadModal({ ...platform, filename, url });
  };

  const handleConfirmDownload = () => {
    if (!downloadModal?.url) {
      setDownloadModal(null);
      return;
    }
    const a = document.createElement('a');
    a.href = downloadModal.url;
    a.download = downloadModal.filename;
    a.rel = 'noopener noreferrer';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setDownloadModal(null);
  };

  return (
    <div className="overflow-x-hidden">
      <section className="relative pt-28 pb-12 sm:pt-36 sm:pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-mesh" />
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-2xl rounded-xl overflow-hidden border border-dark-border bg-dark-card mb-8 shadow-lg">
            <video
              ref={videoRef}
              controls
              playsInline
              className="w-full aspect-video object-cover"
              poster="/images/silly-dragon-demo-poster.jpg"
            >
              <source src="/videos/silly-dragon-demo.mp4" type="video/mp4" />
              <source src="/videos/silly-dragon-demo.mov" type="video/quicktime" />
              {t('sillyDragonPage.videoUnsupported')}
            </video>
            {videoError && (
              <p className="text-sm text-gray-500 px-4 py-3 bg-dark-surface/50 border-t border-dark-border">
                {t('sillyDragonPage.videoLoadHint')}
              </p>
            )}
          </div>
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight mb-4 text-white flex items-center gap-2">
            <Sparkles className="h-6 w-6 sm:h-7 sm:w-7 text-accent flex-shrink-0" />
            {t('sillyDragonPage.title')}
          </h1>
          <p className="text-base md:text-lg text-gray-400 leading-relaxed mb-6">
            {t('sillyDragonPage.body')}
          </p>
          <div className="rounded-xl border border-dark-border bg-dark-card p-5 sm:p-6 mb-8">
            <h2 className="text-lg font-semibold text-white mb-1">{t('sillyDragonPage.earlyAccessTitle')}</h2>
            <p className="text-sm text-gray-400 mb-4">{t('sillyDragonPage.earlyAccessIntro')}</p>
            <ul className="space-y-2 text-sm text-gray-300">
              <li className="flex items-center gap-2">
                <span className="text-accent">∙</span>
                {t('sillyDragonPage.earlyAccessBullet1')}
              </li>
              <li className="flex items-center gap-2">
                <span className="text-accent">∙</span>
                {t('sillyDragonPage.earlyAccessBullet2')}
              </li>
              <li className="flex items-center gap-2">
                <span className="text-accent">∙</span>
                {t('sillyDragonPage.earlyAccessBullet3')}
              </li>
              <li className="flex items-center gap-2">
                <span className="text-accent">∙</span>
                {t('sillyDragonPage.earlyAccessBullet4')}
              </li>
            </ul>
          </div>
          <div className="rounded-xl border border-dark-border bg-dark-card p-5 sm:p-6 mb-8">
            <h2 className="text-lg font-semibold text-white mb-1">{t('sillyDragonPage.aboutTitle')}</h2>
            <p className="text-sm text-gray-400 mb-4">{t('sillyDragonPage.aboutSubtitle')}</p>
            <ul className="space-y-2 text-sm text-gray-300">
              <li>• {t('sillyDragonPage.aboutBrain')}</li>
              <li>• {t('sillyDragonPage.aboutClaw')}</li>
              <li>• {t('sillyDragonPage.aboutEye')}</li>
              <li>• {t('sillyDragonPage.aboutEar')}</li>
              <li>• {t('sillyDragonPage.aboutBody')}</li>
            </ul>
            <p className="text-xs text-gray-500 mt-4">{t('sillyDragonPage.aboutVersion')}</p>
          </div>
          {/* Download modal */}
          {downloadModal && (
            <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" role="dialog" aria-modal="true" aria-labelledby="download-modal-title" onClick={() => setDownloadModal(null)}>
              <div className="bg-dark-card border border-dark-border rounded-2xl shadow-xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
                <h2 id="download-modal-title" className="text-lg font-semibold text-white mb-3">{t('sillyDragonPage.downloadModalTitle')}</h2>
                <p className="text-gray-400 text-sm mb-2">{t('sillyDragonPage.downloadModalDetected')}</p>
                <p className="text-accent font-medium mb-1">{t(`sillyDragonPage.${downloadModal.labelKey}`)}</p>
                {downloadModal.filename && (
                  <p className="text-gray-500 text-xs mb-6 font-mono">{downloadModal.filename}</p>
                )}
                {downloadModal.filename ? (
                  <div className="flex gap-3 justify-end">
                    <button type="button" onClick={() => setDownloadModal(null)} className="px-4 py-2.5 border border-dark-border text-gray-400 rounded-xl hover:bg-dark-surface transition-colors text-sm">
                      {t('sillyDragonPage.downloadModalCancel')}
                    </button>
                    <button type="button" onClick={handleConfirmDownload} className="px-4 py-2.5 bg-accent hover:bg-accent-hover text-dark font-medium rounded-xl transition-colors text-sm">
                      {t('sillyDragonPage.downloadModalConfirm')}
                    </button>
                  </div>
                ) : (
                  <>
                    <p className="text-gray-500 text-xs mb-4">{t('sillyDragonPage.downloadOtherMessage')}</p>
                    <button type="button" onClick={() => setDownloadModal(null)} className="w-full py-2.5 px-4 border border-dark-border text-gray-400 rounded-xl hover:bg-dark-surface transition-colors text-sm">
                      {t('sillyDragonPage.downloadModalCancel')}
                    </button>
                  </>
                )}
              </div>
            </div>
          )}

          <div className="flex flex-wrap gap-3">
            <button
              type="button"
              onClick={handleDownloadClick}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-accent hover:bg-accent-hover text-dark font-medium text-sm rounded-full transition-all duration-300"
            >
              <Download className="h-4 w-4" />
              {t('sillyDragonPage.download')}
            </button>
            <Link
              to="/#projects"
              className="inline-flex items-center gap-2 px-5 py-2.5 text-accent font-medium text-sm rounded-full border border-accent/30 hover:bg-accent/10 transition-all duration-300"
            >
              {t('sillyDragonPage.backToProjects')} <ChevronRight className="h-4 w-4" />
            </Link>
            <Link
              to="/careers"
              className="shimmer-wrap inline-flex items-center gap-2 px-5 py-2.5 text-gray-300 font-medium text-sm rounded-full border border-white/[0.12] hover:bg-white/[0.06] hover:border-white/25 hover:text-white transition-all duration-300"
            >
              {t('footer.about.join')}
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SillyDragon;
